import React, { useContext, useState } from "react";
import { AiOutlineFilePdf } from "react-icons/ai";
import { BiPencil } from "react-icons/bi";
import { FaClone } from "react-icons/fa";
import { HiOutlineEye, HiOutlineTrash } from "react-icons/hi";
import { MdOutlineNotStarted } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import {
  USER_ROLE,
  PARENT_FORM_CATEGORY_SLUG,
} from "../../../Constants/constant";
import { roleAccess } from "../../../helpers/RolesPermission";
import { parseBooleanVal } from "../../../Utils/utils";
import Loader from "../../common/Loader/Loader";
import DeleteFormModal from "./DeleteFormModal";
import DeleteWorkerFormModal from "./DeleteWorkerFormModal";
import { LoginContext } from "helpers/LoginContext";
import { BLK_PORTAL_PREMISSION_KEYS_CHILDREN } from "../../../store/constants";
import SetExpiryDateModal from "./SetExpiryDateModal";
import { BsCalendar2Date } from "react-icons/bs";
import { isCustomFormCategorySlug } from "components/DynamicForms/shared/formCategoryUtils";

/** CRM categories where frontline workers may edit/continue when can_edit is true. */
const WORKER_OPEN_CRM_FORM_CATEGORY_NAMES = [
  PARENT_FORM_CATEGORY_SLUG.HIGH_CARE.NAME,
  PARENT_FORM_CATEGORY_SLUG.MEDICATION.NAME,
  PARENT_FORM_CATEGORY_SLUG.INJURY_MANAGEMENT.NAME,
  PARENT_FORM_CATEGORY_SLUG.SUPPORT_PLAN.NAME,
  PARENT_FORM_CATEGORY_SLUG.PERSON_CENTERED_PLAN.NAME,
  PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.NAME,
  PARENT_FORM_CATEGORY_SLUG.Other.NAME,
];

const WORKER_OPEN_CRM_FORM_CATEGORY_SLUGS = new Set(
  WORKER_OPEN_CRM_FORM_CATEGORY_NAMES.map(
    (name) =>
      Object.values(PARENT_FORM_CATEGORY_SLUG).find((entry) => entry.NAME === name)
        ?.SLUG
  )
    .filter(Boolean)
    .map((slug) => String(slug).toLowerCase())
);

const spStandardUserCanManageSubmittedCrmForm = (item, parentSlug) => {
  const categoryName = item?.category_name;
  const normalizedParentSlug = String(parentSlug ?? "")
    .trim()
    .toLowerCase();
  if (
    normalizedParentSlug &&
    WORKER_OPEN_CRM_FORM_CATEGORY_SLUGS.has(normalizedParentSlug)
  ) {
    return true;
  }
  if (
    categoryName &&
    WORKER_OPEN_CRM_FORM_CATEGORY_NAMES.includes(categoryName)
  ) {
    return true;
  }
  if (isCustomFormCategorySlug(parentSlug)) {
    return true;
  }
  if (
    roleAccess(
      [],
      "participant",
      BLK_PORTAL_PREMISSION_KEYS_CHILDREN.SUBMITTED.FORM.EDIT_ASSESSMENT_FORM
    ) &&
    categoryName === PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.NAME
  ) {
    return true;
  }
  if (
    roleAccess(
      [],
      "participant",
      BLK_PORTAL_PREMISSION_KEYS_CHILDREN.SUBMITTED.FORM.EDIT_EXIT_OR_TRANSITION
    ) &&
    categoryName === PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.NAME
  ) {
    return true;
  }
  if (
    roleAccess(
      [],
      "participant",
      BLK_PORTAL_PREMISSION_KEYS_CHILDREN.SUBMITTED.FORM
        .EDIT_RISK_MANAGEMENT_FORM
    ) &&
    categoryName === PARENT_FORM_CATEGORY_SLUG.RISK_MANAGEMENT.NAME
  ) {
    return true;
  }
  return false;
};

const FormsActionButtons = (props) => {
  const {
    role,
    item,
    parentSlug,
    participantsUid,
    participantId,
    selectedPdfId,
    handleDownload,
    refreshFormList,
    refreshProgressBar,
  } = props;

  const { isUserGracePeriodOver } = useContext(LoginContext);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedIncompleteForm, setselectedIncompleteForm] = useState({});
  const [openExpiryModal, setOpenExpiryModal] = useState(false);

  const navigate = useNavigate();

  const handleCloneInitialAssForm = (uuid) => {
    navigate(
      `/view-template/${participantsUid}/${parentSlug}/initial-assessment-form`,
      {
        state: {
          isCloneFormUUID: uuid,
        },
      }
    );
  };

  const viewPathPrefix =
    role === "sil"
      ? "sil-accommodation"
      : role === "hrm"
      ? "worker-profile"
      : roleAccess([USER_ROLE.Sp_Standard_User])
      ? "participant-profile"
      : "sp-profile";

  return (
    <>
      <div className="form-list-actions">
        {parseBooleanVal(item.is_complete) &&
          !roleAccess([USER_ROLE.Sp_Standard_User]) && (
            <div
              className="icon-calendar-wrapper cursor-pointer"
              onClick={() => setOpenExpiryModal(true)}
              title="Set expiry date"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setOpenExpiryModal(true);
              }}
            >
              <BsCalendar2Date className="icon-size" />
            </div>
          )}

        <Link
          to={`/${viewPathPrefix}/${parentSlug}/${item.uuid}/view`}
          state={{ particpantUuid: participantsUid }}
          className="text-indigo-600 hover:text-indigo-900"
          title="View form"
        >
          <div className="icon-eye-wrapper">
            <HiOutlineEye className="icon-size" />
          </div>
        </Link>

        {parseBooleanVal(item.is_complete) && item?.can_edit ? (
          roleAccess([USER_ROLE.Sp_Standard_User]) &&
          spStandardUserCanManageSubmittedCrmForm(item, parentSlug) ? (
            <Link
              role="button"
              to={`${
                role === "sil"
                  ? "/sil-accommodation"
                  : role === "hrm"
                  ? "/worker-profile"
                  : "/participant-profile"
              }/${parentSlug}/${item.uuid}/update`}
              state={{ particpantUuid: participantsUid }}
              className="text-indigo-600 hover:text-indigo-900"
              title="Edit form"
            >
              <div className="icon-pencil-wrapper">
                <BiPencil className="icon-size" />
              </div>
            </Link>
          ) : (
            !roleAccess([USER_ROLE.Sp_Standard_User]) &&
            item?.can_edit && (
              <Link
                role="button"
                to={`${
                  role === "sil"
                    ? "/sil-accommodation"
                    : role === "hrm"
                    ? "/worker-profile"
                    : "/sp-profile"
                }/${parentSlug}/${item.uuid}/update`}
                state={{
                  participantId: participantId,
                  particpantUuid: participantsUid,
                }}
                className="text-indigo-600 hover:text-indigo-900"
                title="Edit form"
              >
                <div className="icon-pencil-wrapper">
                  <BiPencil className="icon-size" />
                </div>
              </Link>
            )
          )
        ) : null}

        {item?.can_edit &&
          !parseBooleanVal(item.is_complete) &&
          roleAccess([
            USER_ROLE.Sp_Standard_User,
            USER_ROLE.Sp_Admin,
            USER_ROLE.Sp_manager,
          ]) &&
          ((roleAccess(
            [],
            "participant",
            BLK_PORTAL_PREMISSION_KEYS_CHILDREN.SUBMITTED.FORM.ADD
          ) &&
            item?.category_name ===
              PARENT_FORM_CATEGORY_SLUG.PARTICIPANT_ASSESSMENT.NAME) ||
            (roleAccess(
              [],
              "participant",
              BLK_PORTAL_PREMISSION_KEYS_CHILDREN.SUBMITTED.FORM
                .EXIT_OR_TRANSITION
            ) &&
              item?.category_name ===
                PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.NAME) ||
            spStandardUserCanManageSubmittedCrmForm(item, parentSlug) ||
            role === "sil" ||
            role === "hrm") && (
            <Link
              to={`/view-template/${
                role === "sil" ? "sil/" : role === "hrm" ? "hrm/" : ""
              }${participantsUid}/${parentSlug}/${item.uuid}/${window.btoa(
                encodeURIComponent("false")
              )}`}
              className="text-indigo-600 hover:text-indigo-900"
              title="Continue form"
            >
              <div className="icon-stopPlay-wrapper">
                <MdOutlineNotStarted className="icon-size" />
              </div>
            </Link>
          )}

        {!roleAccess([USER_ROLE.Sp_Standard_User]) && (
          <button
            type="button"
            onClick={() => {
              if (isUserGracePeriodOver) return;
              setselectedIncompleteForm(item);
              setOpenDeleteModal(true);
            }}
            title="Delete form"
            className="inline-flex"
          >
            <div className="icon-delete-wrapper">
              <HiOutlineTrash className="icon-size" />
            </div>
          </button>
        )}

        {item.title && parseBooleanVal(item.is_complete) && (
          <>
            {selectedPdfId === item.uuid ? (
              <div>
                <Loader cssClass="text-black mb-1" />
              </div>
            ) : (
              <div
                onClick={() => handleDownload(item, role)}
                className="icon-filePdf-wrapper cursor-pointer"
                title="Download PDF"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    handleDownload(item, role);
                }}
              >
                <AiOutlineFilePdf className="icon-size" />
              </div>
            )}
          </>
        )}

        {item.title &&
          item.title.toLowerCase() === "initial assessment form" &&
          parseBooleanVal(item.is_complete) &&
          roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) && (
            <div
              onClick={() => handleCloneInitialAssForm(item.uuid)}
              className="icon-clone-wrapper cursor-pointer"
              title="Clone form"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  handleCloneInitialAssForm(item.uuid);
              }}
            >
              <FaClone className="icon-size" />
            </div>
          )}
      </div>

      {openDeleteModal && (
        <>
          {parentSlug === "worker-profile" || role === "hrm" ? (
            <DeleteWorkerFormModal
              open={openDeleteModal}
              setOpen={setOpenDeleteModal}
              isUserGracePeriodOver={isUserGracePeriodOver}
              handleClose={() => setOpenDeleteModal(false)}
              uuid={selectedIncompleteForm.uuid}
              refreshFormList={refreshFormList}
              refreshProgressBar={refreshProgressBar}
              role={role}
              iscomplete={item.is_complete}
            />
          ) : (
            <DeleteFormModal
              open={openDeleteModal}
              setOpen={setOpenDeleteModal}
              isUserGracePeriodOver={isUserGracePeriodOver}
              handleClose={() => setOpenDeleteModal(false)}
              uuid={selectedIncompleteForm.uuid}
              refreshFormList={refreshFormList}
              role={role}
              iscomplete={item.is_complete}
            />
          )}
        </>
      )}
      {openExpiryModal && (
        <SetExpiryDateModal
          open={openExpiryModal}
          setOpen={setOpenExpiryModal}
          handleClose={() => setOpenExpiryModal(false)}
          uuid={item.uuid}
          refreshFormList={refreshFormList}
          participantId={participantId}
          currentExpiryDate={item.expiry_date}
          role={role}
        />
      )}
    </>
  );
};

export default FormsActionButtons;
